import { Agent } from '@atproto/api'
import type { OAuthSession } from '@atproto/oauth-client-browser'
import { mapFeedViewPost } from '@/services/postsService'
import type { PostsPage } from '@/types/posts'
import type {
    BlueskyList,
    BlueskyListsPage,
    BlueskyListMember,
    BlueskyListMembersPage,
    ListPurpose,
} from '@/types/lists'

function rkeyFromUri(uri: string): string {
    const parts = uri.split('/')
    const rkey = parts[parts.length - 1]
    if (!rkey) throw new Error(`Malformed AT URI: ${uri}`)
    return rkey
}

function mapListPurpose(purpose: string): ListPurpose {
    if (purpose === 'app.bsky.graph.defs#modlist') return 'modlist'
    return 'curatelist'
}

function mapListView(list: {
    uri: string
    name: string
    description?: string
    avatar?: string
    purpose: string
    listItemCount?: number
    creator: {
        did: string
        handle: string
        displayName?: string
        avatar?: string
    }
    indexedAt: string
}): BlueskyList {
    return {
        uri: list.uri,
        name: list.name,
        description: list.description,
        avatar: list.avatar,
        purpose: mapListPurpose(list.purpose),
        listItemCount: list.listItemCount,
        creator: {
            did: list.creator.did,
            handle: list.creator.handle,
            displayName: list.creator.displayName,
            avatarUrl: list.creator.avatar,
        },
        indexedAt: list.indexedAt,
    }
}

export async function fetchActorLists(
    session: OAuthSession,
    actor: string,
    options?: { cursor?: string; limit?: number }
): Promise<BlueskyListsPage> {
    const agent = new Agent(session)
    const response = await agent.app.bsky.graph.getLists({
        actor,
        limit: options?.limit ?? 50,
        cursor: options?.cursor,
    })

    return {
        lists: response.data.lists.map(mapListView),
        cursor: response.data.cursor,
    }
}

export async function fetchListFeed(
    session: OAuthSession,
    listUri: string,
    options?: { cursor?: string; limit?: number }
): Promise<PostsPage> {
    const agent = new Agent(session)
    const response = await agent.app.bsky.feed.getListFeed({
        list: listUri,
        limit: options?.limit ?? 50,
        cursor: options?.cursor,
    })

    return {
        posts: response.data.feed.map(mapFeedViewPost),
        cursor: response.data.cursor,
    }
}

export async function fetchListByUri(session: OAuthSession, listUri: string): Promise<BlueskyList> {
    const agent = new Agent(session)
    const response = await agent.app.bsky.graph.getList({
        list: listUri,
        limit: 1,
    })
    return mapListView(response.data.list)
}

export async function fetchListMembers(
    session: OAuthSession,
    listUri: string,
    options?: { cursor?: string; limit?: number }
): Promise<BlueskyListMembersPage> {
    const agent = new Agent(session)
    const response = await agent.app.bsky.graph.getList({
        list: listUri,
        limit: options?.limit ?? 50,
        cursor: options?.cursor,
    })

    const members: BlueskyListMember[] = response.data.items.map(item => ({
        uri: item.uri,
        did: item.subject.did,
        handle: item.subject.handle,
        displayName: item.subject.displayName,
        avatarUrl: item.subject.avatar,
    }))

    return {
        members,
        cursor: response.data.cursor,
    }
}

export async function createList(
    session: OAuthSession,
    name: string,
    options?: { description?: string; purpose?: ListPurpose }
): Promise<{ uri: string; cid: string }> {
    const agent = new Agent(session)
    const purpose =
        options?.purpose === 'modlist'
            ? 'app.bsky.graph.defs#modlist'
            : 'app.bsky.graph.defs#curatelist'

    const response = await agent.com.atproto.repo.createRecord({
        repo: session.did,
        collection: 'app.bsky.graph.list',
        record: {
            $type: 'app.bsky.graph.list',
            name,
            purpose,
            description: options?.description,
            createdAt: new Date().toISOString(),
        },
    })

    return { uri: response.data.uri, cid: response.data.cid }
}

export async function deleteList(session: OAuthSession, listUri: string): Promise<void> {
    const agent = new Agent(session)
    await agent.com.atproto.repo.deleteRecord({
        repo: session.did,
        collection: 'app.bsky.graph.list',
        rkey: rkeyFromUri(listUri),
    })
}

export async function addUserToList(
    session: OAuthSession,
    listUri: string,
    did: string
): Promise<{ uri: string; cid: string }> {
    const agent = new Agent(session)
    const response = await agent.com.atproto.repo.createRecord({
        repo: session.did,
        collection: 'app.bsky.graph.listitem',
        record: {
            $type: 'app.bsky.graph.listitem',
            list: listUri,
            subject: did,
            createdAt: new Date().toISOString(),
        },
    })

    return { uri: response.data.uri, cid: response.data.cid }
}

export async function removeUserFromList(
    session: OAuthSession,
    listItemUri: string
): Promise<void> {
    const agent = new Agent(session)
    await agent.com.atproto.repo.deleteRecord({
        repo: session.did,
        collection: 'app.bsky.graph.listitem',
        rkey: rkeyFromUri(listItemUri),
    })
}

function isInvalidSwapError(err: unknown): boolean {
    if (typeof err !== 'object' || err === null) return false
    // @atproto/xrpc's XRPCError coerces HTTP 409 to the 400 enum value, so the
    // swap-mismatch signal is the lexicon error code, never `.status === 409`.
    return (err as { error?: string }).error === 'InvalidSwap'
}

/**
 * Update a list's name/description, guarded by `swapRecord` (the CID from the
 * read) so a concurrent edit from another device can't be silently clobbered.
 * On a conflict we re-read and re-apply the submitted fields once, so only
 * fields *outside* the submitted change are protected — a concurrent edit to
 * name/description itself remains last-writer-wins.
 */
export async function updateList(
    session: OAuthSession,
    listUri: string,
    updates: { name?: string; description?: string }
): Promise<void> {
    const agent = new Agent(session)
    const rkey = rkeyFromUri(listUri)

    // First fetch the existing record to preserve fields we're not updating
    const read = async (): Promise<{ cid: string; value: Record<string, unknown> }> => {
        const existing = await agent.com.atproto.repo.getRecord({
            repo: session.did,
            collection: 'app.bsky.graph.list',
            rkey,
        })
        // An existing record always comes back with a CID. If it somehow
        // doesn't, fail closed rather than falling back to an unguarded write —
        // this is update-only, so there is no legitimate "no record yet" case.
        if (!existing.data.cid) {
            throw new Error('Cannot update list: record read returned no CID')
        }
        return {
            cid: existing.data.cid,
            value: existing.data.value as Record<string, unknown>,
        }
    }

    const put = async (current: { cid: string; value: Record<string, unknown> }) => {
        await agent.com.atproto.repo.putRecord({
            repo: session.did,
            collection: 'app.bsky.graph.list',
            rkey,
            record: {
                ...current.value,
                name: updates.name ?? current.value.name,
                description: updates.description ?? current.value.description,
            },
            swapRecord: current.cid,
        })
    }

    try {
        await put(await read())
    } catch (err: unknown) {
        if (!isInvalidSwapError(err)) throw err
        // Exactly one bounded retry: re-read, re-apply, re-put. A second
        // conflict propagates rather than looping.
        await put(await read())
    }
}
